import React, { useState, useEffect } from "react";
import RectButton from "../customized/RectButton";
import getCheckOut from "../hooks/getCheckOut";
import { trackPageView, getSessionId, getUserId } from "../utils/pageTracking";
import { useWebContents } from "../hooks/useWebContents";
import { useImages } from "../hooks/useImages";
import { renderContentWithLineBreaks } from "../utils/renderContentWithLineBreaks";

const GUIDS = {
  serviceArea: '4c959935-a9fc-4cbf-a734-5b30960030f8',
  dropOff: 'c5c0393b-ba22-4142-9dfa-fd18e800d278',
  curbside:  '879f1769-b90e-4ee2-adab-c1016409068e'
}

const SignUpForm: React.FC = () => {
  const contents = useWebContents(GUIDS, 'SignUp');
  const serviceAreaImage = useImages(GUIDS.serviceArea)?.image;

  const [form, setForm] = useState({
    email: "",
    dropOffLocation: ""
  });

  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState({
    curbside: false,
    dropoff: false
  });

  useEffect(() => {
    // Track page view
    trackPageView('SignUp', getSessionId(), getUserId());

    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Send form data to your backend or email service
    setSubmitted(true);
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center flex items-center justify-center mt-24 md:mt-0"
      style={{
        backgroundImage: `url(${serviceAreaImage})`,
      }}
    >
      <div className="max-w-3xl w-full bg-white/90 rounded-xl shadow-lg py-10 px-4 mx-2 mt-32 mb-12">
        {/* Service Area Title */}
        <h1
          className="text-5xl md:text-6xl font-bold text-center mb-4"
          style={{
            fontFamily: "'Courier New', Courier, monospace",
            color: "#a0522d",
          }}
        >
          {contents.serviceArea.heading}
        </h1>

        {/* Description */}   
        <p className="text-center text-lg text-gray-800 mb-8">
          {(() => {
            const text = contents.serviceArea.content;
            const parts = text.split('@dropOffLocations');
            const afterDropOff = parts[1] || "";
            const emailParts = afterDropOff.split('@emailList');
            return (
              <>
                {renderContentWithLineBreaks(parts[0])}
                <a
                  href="/#/drop-off-locations"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-orange-700 hover:text-orange-900"
                >
                  drop off locations
                </a>
                {(emailParts[0])}
                <a
                  href="/#/#stayInTheKnow"
                  className="underline text-orange-700 hover:text-orange-900"
                >
                  email list
                </a>
                {renderContentWithLineBreaks(emailParts[1])}
              </>
            );
          })()}
        </p>

        <h2 className="text-3xl font-extrabold text-center mb-8">Fill Out Your Email</h2>
        {/* Sign Up Form */}
        {!submitted ? (
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="max-w-md mx-auto">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleChange}
                pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,3}$"
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent mb-4"
                placeholder="your@email.com"
              />

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Drop Off Location (for Drop Off service) *
              </label>
              <select
                name="dropOffLocation"
                required
                value={form.dropOffLocation}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent mb-4"
              >
                <option value="">Select a location...</option>
                <option value="rize">Rize</option>
                <option value="northwood">Northwood Portable Buildings</option>
              </select>

            </div>
          </form>
        ) : (
          <div className="text-green-700 text-center font-semibold mb-8">
            Thank you! Your email has been submitted.
          </div>
        )}
        {/* Select Type of Service */}
        <h2 className="text-3xl font-extrabold text-center mb-8">Select Type Of Service</h2>
        <div className="flex flex-col md:flex-row justify-center gap-10 mb-8">
          {/* Curbside Pickup */}
          <div className="flex flex-col items-center w-full md:w-80">
            <RectButton
              children={isLoading.curbside ? "Processing..." : contents.curbside.heading}
              className="text-white"
              onClick={async () => {
                if (isLoading.curbside || isLoading.dropoff) return;

                const emailRegex = /[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,3}$/;
                if (!form.email || !emailRegex.test(form.email)) {
                  alert("Please enter a valid email address first.");
                  return;
                }

                setIsLoading(prev => ({ ...prev, curbside: true }));
                try {
                  const response = await getCheckOut("curbside", form.email);
                  const data = await response.json();
                  console.log(data);
                  window.location.href = data.url; // Adjust based on your API response structure
                } catch (error) {
                  console.error('Error:', error);
                  alert('An error occurred. Please try again.');
                } finally {
                  setIsLoading(prev => ({ ...prev, curbside: false }));
                }
              }}
            />

            <div className="text-center text-black font-semibold text-base">
             {renderContentWithLineBreaks(contents.curbside.content)}
            </div>
          </div>
          {/* Drop Off */}
          <div className="flex flex-col items-center w-full md:w-80">
            <RectButton
              children={isLoading.dropoff ? "Processing..." : contents.dropOff.heading}
              className="text-white"
              onClick={async () => {
                if (isLoading.curbside || isLoading.dropoff) return;

                // Check if email is valid before proceeding
                const emailRegex = /[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,3}$/;
                if (!form.email || !emailRegex.test(form.email)) {
                  alert("Please enter a valid email address first.");
                  return;
                }

                // Check if drop-off location is selected
                if (!form.dropOffLocation) {
                  alert("Please select a drop-off location first.");
                  return;
                }

                setIsLoading(prev => ({ ...prev, dropoff: true }));
                try {
                  const response = await getCheckOut("dropoff", form.email, form.dropOffLocation);
                  const data = await response.json();
                  console.log(data);
                  window.location.href = data.url; // Adjust based on your API response structure
                } catch (error) {
                  console.error('Error:', error);
                  alert('An error occurred. Please try again.');
                } finally {
                  setIsLoading(prev => ({ ...prev, dropoff: false }));
                }
              }}
            />

            <div className="text-center text-black font-semibold text-base">
              {renderContentWithLineBreaks(contents.dropOff.content)}
            </div>
          </div>
        </div>
      </div>
    </div >


  );
};

export default SignUpForm;

